/*eslint no-fallthrough: ["error", { "commentPattern": "break[\\s\\w]*omitted" }]*/

import React, {
  useState,
  useEffect,
  useRef,
  FC,
  ReactNode,
  MutableRefObject,
  useCallback,
} from "react";
import Spinner from "react-spinkit";

import "./styles.scss";

import CardScanApi from "../api/cardApi";
import Card, { CardFields, CardState, ProgressInfo } from "../api/cardApiModel";
import { CardScanError } from "../api/cardApiError";
import {
  Messages,
  IndicatorOptions,
  InternalOptions,
  TCameraOptions,
} from "./scanview.types";
import { LogLevel, GlobalLogger } from "./debugLogger";

import { defaultIndicatorOptions, defaultMessages } from "./scanview.data";

import detectorWrapper from "./detector";
import DebugMenu from "./debugMenu";
import CameraPermissionModal, {
  CameraPermissionModalConfig,
} from "./permissionModal";
import ScanFrame from "./scanFrame";
import ScanVideoViewer from "./scanVideoViewer";
import CardCapture from "./cardCapture";
import ScanCanvas from "./scanCanvas";

import useCustomWidget from "./hooks/useCustomWidget";
import useLayoutHeight from "./hooks/useLayoutHeight";
import useBufferSize from "./hooks/useBufferSize";
import useVideoSizes from "./hooks/useVideoSizes";
import useRect from "./hooks/useRect";
import useInterval from "./hooks/useInterval";
import { MyGlobalContext } from "./ScanViewerContext";

import { isMobile } from "react-device-detect";
import { useMachine } from "@xstate/react";
import scanViewStateMachine from "./scanViewStateMachine";

import { scanHandler } from "./scanCanvasScanHelper";

import { _handleError } from "./scanViewErrorHandlerHelper";

import { cleanUp } from "./scanViewCleanUpHelper";

import { webSocketHandler } from "./scanViewWebSocketHelper";

import { resetCorners } from "./scanViewUIHelper";

import CameraButton from "../assets/camera_button_dark.png";

import { CardScanLogoURL, getHeaderTitle } from "./helper";

import styles from "./scanview.module.scss";
import WebToMobileModal, { WebToMobileHandOffConfig } from "./webToMobileModal";
import { EligibilityApiError } from "../api/eligibilityApiError";
import Eligibility, { EligibilityInfo } from "../api/eligibilityApiModel";
import { cardScanPackageVersion } from "../version";
import {
  getDeviceInfo,
  getEnvironmentInfo,
  getRefSize,
} from "../utils/getInfoFunctions";
import { setInLocalStorage } from "./helpers/localStorageHelper";
import { verifyEligibility } from "../utils/eligibilityFunctions";

const DEV_SERVER =
  process.env && process.env.REACT_APP_CARDSCAN_SERVER
    ? process.env.REACT_APP_CARDSCAN_SERVER
    : undefined;

const DEV_WEBSOCKET_SERVER =
  process.env && process.env.REACT_APP_CARDSCAN_WEBSOCKET_SERVER
    ? `wss://${process.env.REACT_APP_CARDSCAN_WEBSOCKET_SERVER}`
    : undefined;

const CardScanView: FC<CardScanViewProps> = ({
  logging,
  live,
  sessionToken,
  onSuccess,
  onEligibilitySuccess,
  onEligibilityError,
  onProgress,
  onScanStart,
  onCancel,
  onError,
  onRetry,
  cameraPermissionModalConfig,
  fullScreen = true,
  backsideSupport,
  eligibility,
  messages = {},
  closeButton,
  successIndicator,
  errorIndicator,
  indicatorOptions,
  autoSwitchActiveColor,
  autoSwitchInactiveColor,
  progressBarColor,
  widgetBackgroundColor,
  messageFontSize,
  messageTextColor,
  messageBackgroundColor,
  webToMobileHandoffConfig,
  _internal,
  cameraOptions = {},
}) => {
  //video, detection and image capture canvas
  const videoRefBg = useRef<HTMLVideoElement>(null);
  const detectionCanvasRef = useRef<HTMLCanvasElement>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement>(null);
  const isCardCreationInProgress = useRef(false);
  const [frameDivRefRect, frameDivRef] = useRect();
  const [isMobileOnLandscape, setIsMobileOnLandscape] = useState(false);
  const [isVideoPortrait, setIsVideoPortrait] = useState(false);
  const scanCountRef = useRef(0) as MutableRefObject<number>;
  const performanceCountRef = useRef(0) as MutableRefObject<number>;

  useEffect(() => {
    const checkOrientation = () => window.innerWidth > window.innerHeight;

    const updateOrientation = () => {
      setIsMobileOnLandscape(checkOrientation() && isMobile);
    };

    updateOrientation();

    window.addEventListener("resize", updateOrientation);

    return () => {
      window.removeEventListener("resize", updateOrientation);
    };
  }, [isMobile]);

  const cardScanApi = useRef<CardScanApi | null>(null);

  //TODO: remove this and move to state machine?
  const cardIdRef = useRef("");
  const eligibilityIdRef = useRef("");

  useEffect(() => {
    let apiEndpoint = DEV_SERVER;
    let wsEndpoint = DEV_WEBSOCKET_SERVER;
    if (_internal?.devAPIEndpoint) {
      apiEndpoint =
        typeof _internal.devAPIEndpoint === "string"
          ? _internal.devAPIEndpoint
          : "https://dev.nop.sh/v1/";
    }

    if (_internal?.devWSEndpoint) {
      wsEndpoint =
        typeof _internal.devWSEndpoint === "string"
          ? _internal.devWSEndpoint
          : "wss://dev-ws.nop.sh";
    }

    GlobalLogger.debug(`Using package v${cardScanPackageVersion}`, "verbose");
    _internal?.webviewVersion
      ? GlobalLogger.debug(
          `Using webview v${_internal.webviewVersion}`,
          "verbose"
        )
      : null;
    _internal?.mobileSdkVersion
      ? GlobalLogger.debug(
          `Using mobileSdk v${_internal?.mobileSdkVersion}`,
          "verbose"
        )
      : null;

    if (sessionToken && !cardScanApi.current) {
      cardScanApi.current = new CardScanApi({
        sessionToken,
        live: live || false,
        apiEndpoint: apiEndpoint,
        wsEndpoint: wsEndpoint,
        webviewVersion: _internal?.webviewVersion,
        mobileSdkVersion: _internal?.mobileSdkVersion,
      });
    }
    GlobalLogger.debug(`Using API Endpoint: ${apiEndpoint}`);
    GlobalLogger.debug(`Using WS Endpoint: ${wsEndpoint}`);
  }, [sessionToken, live, _internal]);

  // Update user UI
  const [showCurtain, setShowCurtain] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [messageState] = useState({
    ...defaultMessages,
    ...messages,
  });

  const [spinnerOptions] = useState({
    ...defaultIndicatorOptions,
    ...indicatorOptions,
  });

  const [stateMachineState, stateMachineSend, stateMachineService] = useMachine(
    scanViewStateMachine,
    {
      context: {
        messageState: messageState,
      },
    }
  );

  //updates the header title on every transition
  stateMachineService.subscribe((state) => {
    stateMachineState.context.headerTitle = getHeaderTitle(
      String(state.value),
      messageState,
      stateMachineState.context.autoScan
    );
  });

  // % of progress in scanning process
  const [scanProgress, setScanProgress] = useState(0.0);
  // Also set server side for max successful scans.
  const softMaxUploads = 12;

  // Card detection parameters & metadata
  const [threshold, setThreshold] = useState(0.9);
  const [laplacianThreshold, setLaplacianThreshold] = useState(0.01);
  const defaultScanFrequency = 250;
  const [scanFreqency, setScanFreqency] = useState(defaultScanFrequency);

  // state to save timestamp of last WS ping
  const pingFrequency = 6500;
  const [lastPing, setLastPing] = useState<Date>(new Date());

  const [videoMetadata, setVideoMetadata] = useState<{ [key: string]: any }>(
    {}
  );

  // Short lived buffer for storing scan images before upload
  const [bufferSize, setBufferSize] = useBufferSize(4);

  const scanCaptureBuffer = useRef<
    { file: File; score: number; laplacian: number }[]
  >([]);

  //debug options
  const [latestScore, setLatestScore] = useState(0.0);
  const [latestLaplacian, setLatestLaplacian] = useState(0.0);
  const [bestScore, setBestScore] = useState(0.0);
  const [bestLaplacian, setBestLaplacian] = useState(0.0);
  const debugCanvasBestRef = useRef<HTMLCanvasElement>(null);
  const debugCanvasLatestRef = useRef<HTMLCanvasElement>(null);
  const [modelState, setModelState] = useState("");
  const [scanDebugState, setScanDebugState] = useState("");
  const isEligibilityCreationInProgress = useRef(false); // needed to not create double eligibility

  useVideoSizes(videoRefBg, setShowCurtain, stateMachineState);

  let videoWidth = videoRefBg?.current?.videoWidth
    ? videoRefBg?.current?.videoWidth
    : 500;

  let videoHeight = videoRefBg?.current?.videoHeight
    ? videoRefBg?.current?.videoHeight
    : 500;

  const scale = Math.sqrt(isMobile ? 0.95 : 0.65); // This scales the area to 95% or 65% of the original

  videoWidth *= scale;
  videoHeight *= scale;

  //A card's usual aspect ratio
  //85.60 × 53.98 mm (3.370 × 2.125 in) with an aspect ratio of 1.586
  const CARD_ASPECT_RATIO = 1.58;
  const device_aspect_ratio: number = videoHeight / videoWidth;

  //if height is much larger than width
  //we fix the dimensions to fit in a regular card's usual aspect ratio
  if (device_aspect_ratio >= CARD_ASPECT_RATIO) {
    videoHeight = videoHeight / device_aspect_ratio;
    videoWidth = videoWidth * CARD_ASPECT_RATIO;
  }

  let videoLayoutHeight =
    videoRefBg?.current?.getBoundingClientRect()?.height || 0;
  videoLayoutHeight =
    videoLayoutHeight > videoHeight ? videoHeight : videoLayoutHeight;

  useLayoutHeight(
    videoRefBg,
    stateMachineState.matches("cameraLoading"),
    videoLayoutHeight
  );

  useCustomWidget(
    autoSwitchActiveColor,
    autoSwitchInactiveColor,
    progressBarColor,
    widgetBackgroundColor,
    messageFontSize,
    messageTextColor,
    messageBackgroundColor,
    cameraOptions
  );

  const getCardMetadata = useCallback(() => {
    const environmentInfo = getEnvironmentInfo();
    const deviceInfo = getDeviceInfo();
    const videoElementInfo = getRefSize(videoRefBg);
    const guideFrameElementInfo = getRefSize(frameDivRef);

    return {
      environmentInfo,
      deviceInfo,
      videoElementInfo,
      guideFrameElementInfo,
      videoMetadata,
      ...(_internal?.webviewVersion
        ? { webviewVersion: _internal.webviewVersion }
        : {}),
      ...(_internal?.mobileSdkVersion
        ? { mobileSdkVersion: _internal.mobileSdkVersion }
        : {}),
    };
  }, [videoMetadata, frameDivRef, videoRefBg]);

  useEffect(() => {
    if (eligibility) {
      try {
        verifyEligibility(eligibility);
      } catch (error) {
        if (error instanceof Error) {
          if (onEligibilityError)
            onEligibilityError({
              type: "eligibilityError",
              message: error.message,
            });
        }
        stateMachineSend("eligibilityError");
      }
    }
  }, [eligibility]);

  //This is the main scanning loop and is enabled once the video has loaded.
  //Toggable with loop duration is set via scanFrequency.
  useInterval(
    () => {
      scanHandler(
        videoRefBg,
        frameDivRef,
        captureCanvasRef,
        detectionCanvasRef,
        debugCanvasLatestRef,
        debugCanvasBestRef,
        scanCountRef,
        performanceCountRef,
        setLatestScore,
        setLatestLaplacian,
        setBestScore,
        setBestLaplacian,
        setModelState,
        setScanDebugState,
        setScanProgress,
        cameraOptions.flipHorizontal,
        cameraOptions.flipVertical,
        scanCaptureBuffer,
        threshold,
        laplacianThreshold,
        onError,
        scanProgress,
        softMaxUploads,
        bufferSize,
        videoMetadata,
        cardScanApi,
        cardIdRef,
        stateMachineState,
        stateMachineSend,
        live ? false : _internal?.debugMenu || false
      );
      wsPing(); //this should be moved to a separate interval
    },
    stateMachineState.context.scannerRunning ? scanFreqency : null
  );

  // Reset lastEvents and lastHashes in every start up
  useEffect(() => {
    setInLocalStorage("lastEvents", []);
    setInLocalStorage("lastHashes", []);
  }, []);

  useEffect(() => {
    if (
      isMobile &&
      !isMobileOnLandscape &&
      videoRefBg.current &&
      videoRefBg.current.videoWidth < videoRefBg.current.videoHeight
    ) {
      setIsVideoPortrait(true);
    } else {
      setIsVideoPortrait(false);
    }
  }, [
    isMobile,
    isMobileOnLandscape,
    videoRefBg.current?.videoWidth,
    videoRefBg.current?.videoHeight,
  ]);

  useEffect(() => {
    webSocketHandler(
      cardScanApi,
      cardIdRef,
      onError,
      stateMachineState,
      stateMachineSend,
      backsideSupport,
      eligibility ? true : false,
      onEligibilityError
    );
  }, [cardScanApi, stateMachineState, stateMachineSend, stateMachineService]);

  //TODO: find a way to reset states more clearly
  useEffect(() => {
    const subscription = stateMachineService.subscribe((state) => {
      GlobalLogger.debug(
        `state machine state: ${state.value} - ${state.event.type}`
      );

      if (state.event.type === "scan.completed") {
        const info: ProgressInfo = {
          cardId: state.context.card_id,
          cardState: state.value as CardState,
          scanCount: scanCountRef.current,
        };

        if (onProgress) {
          onProgress(info);
        }
      }

      if (state.matches("success")) {
        cardScanApi?.current
          ?.getCard(cardIdRef.current)
          .then((res) => {
            const card = new Card(res.data);

            GlobalLogger.log(`scanning completed: c:${card.cardId}`);
            GlobalLogger.debug(
              `scanned card: ${JSON.stringify(card)}`,
              "everything"
            );
            onSuccess(card);
          })
          .then(() => {
            if (eligibilityIdRef.current && onEligibilitySuccess) {
              cardScanApi.current
                ?.getEligibility(eligibilityIdRef.current)
                .then((res) => {
                  GlobalLogger.log(
                    `eligibility completed: e:${eligibilityIdRef.current}`
                  );
                  GlobalLogger.debug(
                    `eligibility payload: ${JSON.stringify(res.data)}`,
                    "everything"
                  );
                  const eligibility = new Eligibility(res.data);
                  if (eligibility.state !== "error")
                    onEligibilitySuccess(eligibility);
                });
            }
          })
          .finally(() => {
            cleanUp(
              scanCaptureBuffer,
              setScanProgress,
              videoRefBg,
              cardScanApi
            );
          });
      }

      //reset before moving to backside
      if (state.matches("backsidePending")) {
        scanCountRef.current = 0;
        scanCaptureBuffer.current = [];
        setScanProgress(0);
        resetCorners();
        GlobalLogger.debug("Flipping to backside scan");
      }

      if (
        state.matches("eligibilityPending") &&
        state.event.type === "card.completed" &&
        eligibility &&
        !isEligibilityCreationInProgress.current
      ) {
        isEligibilityCreationInProgress.current = true;
        cardScanApi.current
          ?.createEligibility(eligibility, cardIdRef.current)
          .then((response) => {
            eligibilityIdRef.current = response.data.eligibility_id;
          })
          .catch((error) => {
            onEligibilityError?.(error);
            isEligibilityCreationInProgress.current = false;
          });
      }

      // on toggle
      if (
        state.context.toggledAutoManual &&
        ["autoCapture", "manualCapture"].some(state.matches)
      ) {
        GlobalLogger.debug("Toggling to manual capture");
        scanCaptureBuffer.current = [];
        setScanProgress(0);
        document.documentElement.style.setProperty(
          "--cardscan-buffer-size",
          "0"
        );
        setScanFreqency(defaultScanFrequency);
      }
    });

    return subscription.unsubscribe;
  }, [stateMachineService]);

  useEffect(() => {
    //de-dupe this or make it lazy.
    //creating and saving card_id.
    if (
      !isCardCreationInProgress.current &&
      stateMachineState.context.modelState === "warmed"
    ) {
      isCardCreationInProgress.current = true;
      const metadata = getCardMetadata();

      cardScanApi.current
        ?.createCard(backsideSupport, true, metadata)
        .then((response) => {
          cardIdRef.current = response.data.card_id;
          GlobalLogger.debug(`Card ID: ${cardIdRef.current}`);

          onScanStart?.(cardIdRef.current, false);

          //register socket on card creation
          if (
            cardScanApi?.current?.socket &&
            cardScanApi?.current?.socket.readyState == 1
          ) {
            // 1 is open
            const payload = JSON.stringify({
              card_id: response.data.card_id,
              action: "register",
              source: "startup",
            });
            cardScanApi.current.socket.send(payload);
          }
        })
        .catch((error) => {
          _handleError(error, stateMachineSend, onError);
          isCardCreationInProgress.current = false;
        });
    }
  }, [
    cardScanApi,
    stateMachineSend,
    onError,
    backsideSupport,
    stateMachineState,
    getCardMetadata,
  ]);

  useEffect(() => {
    // Setup popstate event listener
    const popstateHandler = () => {
      cleanUp(scanCaptureBuffer, setScanProgress, videoRefBg, cardScanApi);
    };
    window.addEventListener("popstate", popstateHandler);

    // Cleanup popstate event listener on component unmount
    return () => {
      window.removeEventListener("popstate", popstateHandler);
    };
  }, []);

  useEffect(() => {
    if (logging) {
      setModelState(detectorWrapper.get().modelState);
    }

    if (live) {
      GlobalLogger.setLogLevel(
        logging === undefined || logging === "off" ? "off" : "normal"
      );
    } else {
      GlobalLogger.setLogLevel(logging || "off");
    }
  }, [logging, live, detectorWrapper]);

  //TODO: move this into cardScanApi w/ own timer and toggle on/off?
  const wsPing = () => {
    const now = new Date();
    const ms = lastPing.getTime();

    if (["success", "error"].some(stateMachineState.matches)) {
      return;
    }

    if (now.getTime() - ms < pingFrequency) {
      return;
    }

    if (
      cardScanApi?.current?.socket &&
      cardScanApi.current.socket.readyState == 1
    ) {
      cardScanApi.current.socket.send("ping");
      setLastPing(now);
    }
  };

  //---------------------------------------
  // UI Interaction handlers
  //---------------------------------------

  //TODO: most of this could be reduced to state machine (but need to add retry state -> autocapture)
  const handleRetryButton = async () => {
    //sanity check we're in retry mode.

    //empty both detection buffer and clear progress UI
    scanCaptureBuffer.current = [];
    scanCountRef.current = 0;
    setScanProgress(0);
    document.documentElement.style.setProperty("--cardscan-buffer-size", "0");
    document.documentElement.style.setProperty(
      "--cardscan-corner-height",
      "20%"
    );
    document.documentElement.style.setProperty(
      "--cardscan-corner-width",
      "20%"
    );

    const video = videoRefBg.current;
    if (video?.paused) {
      await video?.play();
    }

    //reset state.
    stateMachineSend("retry_scan");
    setScanFreqency(defaultScanFrequency);
    cardIdRef.current = "";

    if (onRetry) {
      onRetry();
    }

    const metadata = getCardMetadata();
    //de-dupe this
    cardScanApi.current
      ?.createCard(backsideSupport, true, metadata)
      .then((response) => {
        cardIdRef.current = response.data.card_id;

        onScanStart?.(cardIdRef.current, false);
        //register socket on card creation
        if (cardScanApi?.current?.socket) {
          const payload = JSON.stringify({
            card_id: response.data.card_id,
            action: "register",
            source: "retry",
          });
          cardScanApi.current.socket.send(payload);
        }

        //needs to implement another state coming to "retry"
        stateMachineSend("retry_scan");
      })
      .catch((error) => {
        _handleError(error, stateMachineSend, onError);
      });
  };

  const onCloseBtn = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const getModelState = (state: string) => {
    stateMachineSend({
      type: "UPDATE_MODEL_STATE",
      modelState: state,
    });
  };

  useEffect(() => {
    if (stateMachineState.value === "cameraLoading") {
      const initialModelState = detectorWrapper.get().modelState;

      stateMachineSend({
        type: "UPDATE_MODEL_STATE",
        modelState: initialModelState,
      });

      if (initialModelState !== "warmed") {
        detectorWrapper.get().setModelStatusCallback(getModelState);
      }
    }
  }, [stateMachineState.value]);

  // Reset corners on component mount and unmount
  useEffect(() => {
    resetCorners();

    return () => {
      resetCorners();
    };
  }, []);

  //---------------------------------------
  // Rendering
  //---------------------------------------

  return (
    <MyGlobalContext.Provider
      value={{
        logging: logging || GlobalLogger.startingLogLevel,
        modelState,
        scanDebugState,
        threshold,
        setThreshold,
        laplacianThreshold,
        setLaplacianThreshold,
        scanFreqency,
        setScanFreqency,
        bufferSize,
        setBufferSize,
        bufferRef: scanCaptureBuffer,
        debugCanvasBestRef,
        bestScore,
        bestLaplacian,
        debugCanvasLatestRef,
        latestScore,
        latestLaplacian,
        debugMenu: live ? false : _internal?.debugMenu || false,
        videoMetadata,
        showCurtain,
        onCloseBtn,
        closeButton,
        isMobile,
        isMobileOnLandscape,
        stateMachineState,
        stateMachineSend,
        stateMachineService,
        handleRetryButton,
        showErrorModal,
        cameraPermissionModalConfig,
        webToMobileHandoffConfig,
        webviewVersion: _internal?.webviewVersion ?? null,
        mobileSdkVersion: _internal?.mobileSdkVersion ?? null,
      }}
    >
      <WebToMobileModal
        enableModel={
          showErrorModal &&
          !isMobile &&
          !(cameraPermissionModalConfig?.enabled ?? true) &&
          webToMobileHandoffConfig?.enabled !== undefined
            ? webToMobileHandoffConfig?.enabled
            : false
        }
        cardScanApi={cardScanApi}
        webToMobileHandoffConfig={webToMobileHandoffConfig}
        spinnerOptions={spinnerOptions}
        backsideSupport={backsideSupport}
      />
      <div
        data-cy="cardscanMainContainer"
        className={`${styles.cardscanMain} ${isMobile ? styles.isMobile : ""} ${
          isMobileOnLandscape ? styles.isLandscape : ""
        }`}
      >
        <DebugMenu />
        <CardCapture>
          <div
            className={`${styles.cardscanVideoContainer} ${
              fullScreen && !isMobile ? styles.fullScreenLimit : ""
            } 
            ${isMobile ? styles.mobile : ""}
            ${isMobileOnLandscape ? styles.landscapeMobile : ""}
            ${isVideoPortrait ? styles.portraitView : ""}`}
          >
            <ScanVideoViewer
              videoRef={videoRefBg}
              setShowErrorModal={setShowErrorModal}
              stateMachineSend={stateMachineSend}
              setScanDebugState={setScanDebugState}
              onError={onError}
              captureCanvasRef={captureCanvasRef}
              setVideoMetadata={setVideoMetadata}
              isMobile={isMobile}
              isVideoPortrait={isVideoPortrait}
            />
            {stateMachineState.matches("cameraLoading") ? (
              <div
                data-cy="cardScanCameraLoading"
                className={styles.spinnerWrapper}
              >
                <Spinner {...spinnerOptions} />
              </div>
            ) : (
              <ScanFrame
                successIndicator={successIndicator}
                errorIndicator={errorIndicator}
                handleRetryButton={handleRetryButton}
                frameDivRef={frameDivRef}
                spinnerOptions={spinnerOptions}
                webToMobileHandoffConfig={webToMobileHandoffConfig}
                cardScanApi={cardScanApi}
                backsideSupport={backsideSupport}
              />
            )}
            <img
              data-cy="cardscanLogo"
              alt="cardscan logo"
              src={CardScanLogoURL}
              className={`${styles.cardscanLogo} ${
                isMobile ? styles.cardscanLogoMobile : ""
              }`}
            />
            {!showCurtain && !stateMachineState.matches("cameraLoading") && (
              <>
                {!stateMachineState.context.autoScan &&
                  !stateMachineState.context.processing &&
                  !stateMachineState.matches("success") && (
                    <img
                      data-cy="cameraBtn"
                      alt="button to start the scanning"
                      src={CameraButton}
                      className={styles.cameraBtn}
                      onClick={() => stateMachineSend("MANUAL_BUTTON_CLICK")}
                      style={{ bottom: 0 }}
                    />
                  )}
              </>
            )}
          </div>
        </CardCapture>
      </div>
      <ScanCanvas
        captureCanvasRef={captureCanvasRef}
        detectionCanvasRef={detectionCanvasRef}
        frameDivDimensions={frameDivRefRect}
        frameDivRef={frameDivRef}
        videoRefBg={videoRefBg}
        videoHeight={videoHeight}
        videoWidth={videoWidth}
      />
    </MyGlobalContext.Provider>
  );
};

//TODO: add required vs optional fields.
//Required - card fails without these, when it hits max scans.
//Optional - card keeps scanning if it has a high confidence version of one of these.
//         - does not fail on max scans without them.

export type CardScanViewProps = {
  logging?: LogLevel;
  live?: boolean;
  sessionToken: string;
  fullScreen?: boolean;
  onSuccess: (detail: Card) => void;
  onEligibilitySuccess?: (detail: Eligibility) => void;
  onEligibilityError?: (error: EligibilityApiError) => void;
  onCancel?: () => void;
  onError?: (error: CardScanError) => void;
  onRetry?: () => void;
  onProgress?: (progressInfo: ProgressInfo) => void;
  onScanStart?: (cardId: string, isBackside: boolean) => void;
  messages?: Messages;
  backsideSupport?: boolean;
  eligibility?: EligibilityInfo;
  closeButton?: ReactNode;
  successIndicator?: ReactNode;
  errorIndicator?: ReactNode;
  indicatorOptions?: IndicatorOptions;
  cameraPermissionModalConfig?: CameraPermissionModalConfig;
  autoSwitchActiveColor?: string;
  autoSwitchInactiveColor?: string;
  progressBarColor?: string;
  widgetBackgroundColor?: string;
  messageFontSize?: string;
  messageTextColor?: string;
  messageBackgroundColor?: string;
  webToMobileHandoffConfig?: WebToMobileHandOffConfig;
  _internal?: InternalOptions;
  cameraOptions?: TCameraOptions;
};

export default CardScanView;
